import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import CountUp from 'react-countup'
import { Building2, Users, Award, MapPin, Briefcase, Clock, Target, Shield, Sparkles, TrendingUp, Zap, Star } from 'lucide-react'

const stats = [
  {
    icon: Building2,
    value: 100,
    suffix: '+',
    label: 'Government Projects',
    description: 'Successfully delivered',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  {
    icon: Users,
    value: 50,
    suffix: '+',
    label: 'Government Clients',
    description: 'Trusted partnerships',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
  },
  {
    icon: Award,
    value: 15,
    suffix: '+',
    label: 'Years Excellence',
    description: 'Industry experience',
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
  },
  {
    icon: MapPin,
    value: 20,
    suffix: '+',
    label: 'Districts Served',
    description: 'Pan-Maharashtra presence',
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',
  },
  {
    icon: Briefcase,
    value: 100,
    suffix: 'Cr+',
    label: 'Project Value',
    description: 'Total delivered value',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  {
    icon: Clock,
    value: 98,
    suffix: '%',
    label: 'On-Time Delivery',
    description: 'Commitment to deadlines',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  },
  {
    icon: Target,
    value: 50,
    suffix: '+',
    label: 'Team Members',
    description: 'Skilled professionals',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
  },
  {
    icon: Shield,
    value: 10,
    suffix: '',
    label: 'ISO Certifications',
    description: 'Quality standards',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  },
]

const achievements = [
  { icon: TrendingUp, label: 'Growing', value: 'YoY' },
  { icon: Zap, label: 'Fast Delivery', value: '98%' },
  { icon: Star, label: 'Client Rating', value: '5/5' },
]

export const StatsSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      {/* Background is now global - in Home.tsx */}
      {/* Grid Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="container-enterprise relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
            }}
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm text-slate-300">By the Numbers</span>
            <span className="text-sm font-bold text-primary">({stats.length})</span>
          </motion.div>

          {/* Title */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Proven Track Record
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Numbers that speak for our commitment to excellence and government service delivery.
          </p>

          {/* Achievement Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 mt-8"
          >
            {achievements.map((achievement) => {
              const Icon = achievement.icon
              return (
                <div
                  key={achievement.label}
                  className="flex items-center gap-2 px-4 py-2 rounded-full"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm text-slate-300">{achievement.label}</span>
                  <span className="text-sm font-bold text-white">{achievement.value}</span>
                </div>
              )
            })}
          </motion.div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                whileHover={{ scale: 1.03, y: -8 }}
                className="group relative"
              >
                <div className="relative overflow-hidden rounded-2xl p-6 h-full"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  {/* Hover Glow Effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `radial-gradient(circle at 50% 50%, ${stat.color}15 0%, transparent 70%)`,
                    }}
                  />

                  {/* Number Badge */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ opacity: 1, scale: 1 }}
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
                    style={{
                      background: stat.gradient,
                      boxShadow: `0 0 15px ${stat.color}40`,
                    }}
                  >
                    <span className="text-white">({String(index + 1).padStart(2, '0')})</span>
                  </motion.div>

                  {/* Icon with Glow */}
                  <div className="relative mb-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{
                        background: stat.gradient,
                        boxShadow: `0 0 25px ${stat.color}40`,
                      }}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    {/* Icon Glow */}
                    <div className="absolute inset-0 rounded-xl blur-xl opacity-50"
                      style={{
                        background: stat.gradient,
                      }}
                    />
                  </div>

                  {/* Value with CountUp */}
                  <div className="mb-2">
                    {inView && (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl sm:text-4xl font-bold text-white">
                          <CountUp end={stat.value} duration={2.5} />
                        </span>
                        <span className="text-xl font-bold gradient-text">{stat.suffix}</span>
                      </div>
                    )}
                  </div>

                  {/* Label */}
                  <h3 className="text-base font-semibold text-white mb-1">{stat.label}</h3>

                  {/* Description */}
                  <p className="text-sm text-slate-400">{stat.description}</p>

                  {/* Bottom Accent Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: stat.gradient,
                    }}
                  />
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-slate-400 mb-4">
            Trusted by government organizations across Maharashtra
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['ISO 9001:2015', 'ISO 27001', 'ISO 14001', 'ISO 45001', 'ISO 20000', 'ISO 22000', 'ISO 22301', 'ISO 26000', 'ISO 31000', 'ISO 50001'].map((cert) => (
              <span
                key={cert}
                className="px-4 py-2 text-sm font-medium rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  color: '#a5b4fc',
                }}
              >
                {cert}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}